// The assistant on an activity page: The assistant asks (a team check sent to you about this activity, answered here by
// typing or speaking), and Check-ins (the team checks about it). Telling the AI to change it is the AI sphere at the
// top (6.3, Kareem 5 Nov: the "Tell the AI" box under "What the AI knows about this" was too much AI).
import { useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import type { TeamCheck } from '@dpw/shared';
import { api } from '../api';
import { useChange, useChecks, useChecksToAnswer } from '../lib/queries';
import { canRecord, joinWords, useRecorder } from '../lib/recorder';
import { ago } from '../lib/format';
import { ErrorNote } from './bits';
import { DrawnCheck, Orb } from './motion';
import { Waveform } from './Waveform';

const about = (c: TeamCheck, activityId: string) => c.about?.kind === 'activity' && c.about.id === activityId;

/** A question the team (or the assistant) sent you about this activity: answer it here. */
export function AssistantAsks({ activityId }: { activityId: string }) {
  const q = useChecksToAnswer();
  // A question you just answered stays here (with "Answer sent") until you leave the page.
  const [answered, setAnswered] = useState<Record<string, TeamCheck>>({});
  const open = (q.data ?? []).filter((c): c is TeamCheck => c.view === 'full' && c.status === 'open' && about(c, activityId));
  const shown = [...open, ...Object.values(answered).filter(c => !open.some(o => o.id === c.id))];
  if (!shown.length) return null;
  return <>{shown.map(c => <AskOne key={c.id} c={c} onSent={() => setAnswered(x => ({ ...x, [c.id]: c }))} />)}</>;
}

function AskOne({ c, onSent }: { c: TeamCheck; onSent: () => void }) {
  const [text, setText] = useState('');
  // The card stays on screen from the moment you send (the question leaves the open list once answered).
  const [sent, setSent] = useState(false);
  const answer = useChange((t: string) => api.checks.answer(c.id, t));
  const send = (v: string) => { if (v.trim()) { onSent(); answer.mutate(v.trim(), { onSuccess: () => setSent(true) }); } };
  const rec = useRecorder((t, _demo, o) => { const full = joinWords(text, t); if (o.send) send(full); else setText(full); });
  return (
    <section className="brief asks" aria-label="The assistant asks">
      <span className="k"><Orb size={9} />The assistant asks</span>
      <div className="sm" style={{ fontWeight: 500 }}>{c.question}</div>
      <AnimatePresence mode="wait" initial={false}>
        {sent ? <motion.span key="s" className="apd" initial={{ opacity: 0 }} animate={{ opacity: 1 }}><DrawnCheck size={15} />Answer sent. {c.askedBy.name.split(' ')[0]} will see it.</motion.span> : (
          <motion.form key="f" className="askmini" onSubmit={e => { e.preventDefault(); if (rec.state === 'recording') rec.stop({ send: true }); else send(text); }}>
            {rec.state === 'recording' ? <span className="recrow"><Waveform bars={rec.bars} level={rec.level} /></span>
              : <input value={text} onChange={e => setText(e.target.value)} placeholder="Your answer" aria-label="Your answer" />}
            {canRecord() && <button type="button" className="bt sm ghost" onClick={() => (rec.state === 'recording' ? rec.stop() : void rec.start())}>{rec.state === 'recording' ? 'Stop' : 'By voice'}</button>}
            <button className="bt sm" disabled={answer.isPending || rec.state === 'working' || (!text.trim() && rec.state !== 'recording')}>Answer</button>
          </motion.form>
        )}
      </AnimatePresence>
      {answer.error ? <ErrorNote error={answer.error} /> : null}
      {rec.error && <div className="xs mut">{rec.error}</div>}
    </section>
  );
}

const STATE: Record<TeamCheck['status'], [string, string]> = {
  open: ['Waiting for answers', 'amb'], in_review: ['With the director', 'amb'], answered: ['Answered', 'grn'], conflict: ['Answers differ', 'red'],
  confirmed: ['Confirmed', 'grn'], expired: ['No answer in time', 'neu'],
};

/** The team checks about this activity: who was asked, and where each stands. */
export function CheckIns({ activityId }: { activityId: string }) {
  const q = useChecks();
  const list = (q.data ?? []).filter((c): c is TeamCheck => c.view === 'full' && about(c, activityId));
  if (!list.length) return null;
  return (
    <div className="panel">
      <div className="sh"><b>Check-ins</b></div>
      {list.slice(0, 5).map(c => (
        <a className="sig2" key={c.id} href={`/checks/${c.id}`}>
          <span className={`dt ${STATE[c.status][1]}`} />
          <span><span className="sm">{c.question}</span><span className="xs mut">{STATE[c.status][0]} · {c.answers.length} of {c.recipients.length} answered · {ago(c.createdAt)}</span></span>
        </a>
      ))}
    </div>
  );
}
